<template>
  <div
    class="body public-company-landing"
    :class="[themeClass, layoutClass]"
  >
    <!-- Navbar with Back Button -->
    <Navbar 
      :showBackButton="true" 
      @back="goBack"
    />
    
    <!-- Hero Section with Company Header -->
    <section id="terms" class="hero-section" :style="heroBackground" style="margin-bottom: 15px;">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="company-logo-container" v-if="companyData?.logo_url">
          <img :src="authStore.proxiedImage(companyData.logo_url)" class="company-logo" />
        </div>
        
        <!-- <div class="company-badge" v-if="companyData">
          <span class="badge verified" v-if="companyData.national_id">✅ {{ $t('companiesLanding.official_company') }}</span>
        </div> -->
        
        <h1 class="company-name">{{ companyData?.name || $t('companiesLanding.company') }}</h1>
        
        <p v-if="companyData?.complete_description" class="company-full-description">
          {{ companyData.complete_description }}
        </p>
        
        <div class="company-meta">
          <div class="meta-item" v-if="companyData?.activity_type">
            <span class="meta-icon">🏢</span>
            <span class="meta-text">{{ companyData.activity_type }}</span>
          </div>
          <!-- <div class="meta-item" v-if="companyData?.country">
            <span class="meta-icon">📍</span>
            <span class="meta-text">{{ companyData.country }}</span>
          </div> -->
          <!-- <div class="meta-item">
            <span class="meta-icon">👥</span>
            <span class="meta-text">{{ formatNumber(companyData?.user_count) }} {{ $t('companiesLanding.users') }}</span>
          </div>
          <div class="meta-item" v-if="companyData?.national_id">
            <span class="meta-icon">🆔</span>
            <span class="meta-text">{{ $t('companiesLanding.national_id') }}: {{ companyData.national_id }}</span>
          </div> -->
        </div>
        
        <!-- Website Link -->
        <div class="website-link" v-if="companyData?.website_url">
          <a :href="companyData.website_url" target="_blank" rel="noopener noreferrer" class="btn-website">
            <span>{{ $t('companiesLanding.more_info') }}</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </a>
        </div>
        
        <!-- Address Info -->
        <div class="address-info" v-if="companyData?.address">
          <span class="address-icon">📍</span>
          <span class="address-text">{{ companyData.address }}</span>
          <span v-if="companyData?.post_code" class="post-code">{{ $t('companiesLanding.postal_code') }}: {{ companyData.post_code }}</span>
        </div>
        
        <!-- Call to Action -->
        <div class="cta-buttons">
          <button 
            v-if="!isAuthenticated" 
            @click="handleLogin" 
            class="btn btn-primary btn-large"
          >
            <span class="btn-icon">🔐</span>
            {{ $t('companiesLanding.login_account') }}
          </button>
          <button 
            v-else 
            @click="goToCompanyDashboard" 
            class="btn btn-success btn-large"
          >
            <span class="btn-icon">🚀</span>
            {{ $t('companiesLanding.enter_panel', { name: companyData?.name }) }}
          </button>
          <button @click="scrollToFeatures" class="btn btn-outline btn-large">
            <span class="btn-icon">ℹ️</span>
            <span style="color: aliceblue;">{{ $t('companiesLanding.learn_more') }}</span>
          </button>
        </div>

        <div v-if="companyLoginUrl" class="company-login-share" dir="rtl">
          <h3> ورود به {{ companyData?.name }}</h3>
          <p>با اسکن این کد، وارد صفحه ورود می‌شوید و پس از ورود، این شرکت به‌صورت خودکار انتخاب می‌شود.</p>
          <a :href="companyLoginQrUrl" target="_blank" rel="noopener noreferrer" aria-label="باز کردن تصویر کد ورود">
            <img :src="companyLoginQrUrl" :alt="`QR ورود به ${companyData?.name}`" class="company-login-qr" />
          </a>
          <button type="button" class="btn btn-outline-light" @click="copyCompanyLoginLink">
            کپی لینک 
          </button>
        </div>
        
        <!-- Saved Company Indicator -->
        <!-- <div v-if="savedCompanyId === companyId" class="saved-indicator">
          <span class="indicator-icon">⭐</span>
          <span>{{ $t('companiesLanding.company_saved') }}</span>
        </div> -->
      </div>
    </section>

    <!-- Stats Section -->
    <section class="stats-section" v-if="companyData && companyData.show_stats">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">{{ formatNumber(companyData.user_count) }}</div>
            <div class="stat-label">{{ $t('companiesLanding.active_users') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ formatNumber(companyData.stats?.active_operators) }}</div>
            <div class="stat-label">{{ $t('companiesLanding.online_operators') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ formatNumber(companyData.stats?.active_categories) }}</div>
            <div class="stat-label">{{ $t('companiesLanding.service_categories') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">۲۴/۷</div>
            <div class="stat-label">{{ $t('companiesLanding.support') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section with Service Images -->
    <section
      class="features-section"
      id="features"
      v-if="hasServices && companyData?.show_services"
    >
      <div class="container">
        <h2 class="section-title">{{ $t('companiesLanding.services_features', { name: companyData?.name }) }}</h2>
        <p class="section-subtitle">{{ companyData?.description }}</p>
        
        <div class="features-grid">
          <div 
            v-for="(service, index) in companyServices" 
            :key="index" 
            class="feature-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="service-image-container" v-if="service.image_url">
              <img :src="authStore.proxiedImage(service.image_url)" class="service-image" />
            </div>
            <div v-else class="feature-icon" :style="{ background: serviceGradients[index % serviceGradients.length] }">
              <span>{{ serviceIcons[index % serviceIcons.length] }}</span>
            </div>
            <h3 class="feature-title">{{ service.title }}</h3>
            <p class="feature-description">{{ service.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Map Section (if location available) -->
    <section class="map-section" v-if="companyData?.show_map && companyData?.location?.lat && companyData?.location?.lng">
      <div class="container">
        <h2 class="section-title">{{ $t('companiesLanding.location', { name: companyData?.name }) }}</h2>
        <div class="map-container">
          <div id="company-map" ref="mapElement" style="height: 400px; width: 100%; border-radius: 12px;"></div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section class="how-it-works">
      <div class="container">
        <h2 class="section-title">{{ $t('companiesLanding.how_to_use', { name: companyData?.name }) }}</h2>
        
        <div class="steps-grid">
          <div class="step-card" v-for="(step, index) in steps" :key="index">
            <div class="step-number">{{ index + 1 }}</div>
            <h3 class="step-title">{{ step.title }}</h3>
            <p class="step-description">{{ step.description }}</p>
          </div>
        </div>
        
        <div class="text-center" v-if="!isAuthenticated">
          <button @click="handleLogin" class="btn btn-primary btn-large">
            {{ $t('companiesLanding.get_started') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Why Section -->
    <section class="trust-section" v-if="hasWhySelectItems && companyData?.show_why_select">
      <div class="container">
        <h2 class="section-title">{{ $t('companiesLanding.why_company', { name: companyData?.name }) }}</h2>
        
        <div class="trust-grid">
          <div 
            class="trust-item"             
            v-for="(item, index) in whySelectItems" 
            :key="index"
          >
            <div class="trust-icon">{{ item.icon }}</div>
            <h4>{{ item.title }}</h4>
            <p>{{ item.description }}</p>
          </div>    
        </div>
      </div>
    </section>

    <!-- Call to Action -->
    <section class="cta-section">
      <div class="container">
        <h2 style="color: gray;">{{ $t('companiesLanding.join_users', { name: companyData?.name }) }}</h2>
        <p>{{ $t('companiesLanding.join_desc') }}</p>
        <div class="cta-buttons">
          <button v-if="!isAuthenticated" @click="handleLogin" class="btn btn-primary btn-large">
            {{ $t('companiesLanding.login_register') }}
          </button>
          <button v-else @click="goToCompanyDashboard" class="btn btn-success btn-large">
            {{ $t('companiesLanding.enter_dashboard') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-info">
            <h4>{{ companyData?.name }}</h4>
            <p v-if="companyData?.national_id">{{ $t('companiesLanding.national_id') }}: {{ companyData.national_id }}</p>
            <p v-if="companyData?.economic_id">{{ $t('companiesLanding.economic_code') }}: {{ companyData.economic_id }}</p>
            <p v-if="companyData?.address">{{ companyData.address }}</p>
            <p v-if="companyData?.website_url">
              <a :href="companyData.website_url" target="_blank" rel="noopener noreferrer">{{ $t('companiesLanding.company_website') }}</a>
            </p>
          </div>
          <div class="footer-links">
            <router-link :to="{ hash: '#terms' }">{{ $t('companiesLanding.terms') }}</router-link>
            <router-link :to="{ hash: '#terms' }">{{ $t('companiesLanding.privacy') }}</router-link>
            <router-link :to="{ hash: '#terms' }">{{ $t('companiesLanding.contact_us') }}</router-link>
          </div>
          <div class="footer-copyright">
            © {{ new Date().getFullYear() }} {{ $t('companiesLanding.all_rights_reserved') }}
          </div>
          <!-- Enamad -->
          <div class="footer-enamad" v-if="companyData?.enamad">
            <div class="enamad-container">
              <a 
                v-if="companyData.enamad.href" 
                referrerpolicy="origin"
                target="_blank"
                :href="companyData.enamad.href"
                class="enamad-link"
              >
                <img 
                  v-if="companyData.enamad.image_source" 
                  referrerpolicy="origin"
                  src="@/assets/Icons/enamad.png" 
                  alt=""
                  style="cursor:pointer"
                  :code="companyData.enamad.code"
                  class="enamad-image"
                />
                <!-- <span v-else class="enamad-code">{{ companyData.enamad.code }}</span> -->
              </a>
              <div v-else class="enamad-info">
                <img 
                  v-if="companyData.enamad.image_source" 
                  referrerpolicy="origin"
                  :src="companyData.enamad.image_source" 
                  alt=""
                  style="cursor:pointer"
                  :code="companyData.enamad.code"
                  class="enamad-image"
                />
                <span v-else-if="companyData.enamad.code" class="enamad-code">{{ companyData.enamad.code }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>

    <!-- Loading State -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
    </div>
    
    <!-- Error State -->
    <div v-if="error" class="error-overlay">
      <div class="error-card">
        <span class="error-icon">⚠️</span>
        <h3>{{ $t('companiesLanding.error_title') }}</h3>
        <p>{{ error }}</p>
        <button @click="goLanding" class="btn btn-primary">
          {{ $t('companiesLanding.back_to_home') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { companyService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'
import nmp from "@neshan-maps-platform/mapbox-gl"

export default {
  name: 'PublicCompanyLanding',
  components: {
    Navbar
  },
  props: {
    companyName: {
      type: String,
      default: ''
    },
    companyId: {
      type: [String, Number],
      default: null
    }
  },
  setup(props) {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    
    const companyData = ref(null)
    const loading = ref(true)
    const error = ref(null)
    const mapElement = ref(null)
    let map = null
    let marker = null
    
    // Use props if available, otherwise fallback to route params
    const effectiveCompanyId = computed(() => props.companyId || route.params.companyId)
    const effectiveCompanyName = computed(() => props.companyName || route.params.companyName)
    
    // Check if user is authenticated
    const isAuthenticated = computed(() => authStore.isAuthenticated)
    
    // Get saved company from localStorage
    const savedCompanyId = ref(localStorage.getItem('lastVisitedCompany'))
    
    // Dynamic services from database
    const companyServices = computed(() => {
      return companyData.value?.services || []
    })
    
    // Check if company has any services
    const hasServices = computed(() => companyServices.value.length > 0)
    
    // Why Select items from database
    const whySelectItems = computed(() => {
      return companyData.value?.why_select_items || []
    })
    
    // Check if company has any why select items
    const hasWhySelectItems = computed(() => whySelectItems.value.length > 0)
    
    // Default icons for why select section
    const defaultIcons = ['🔒', '⚡', '📱', '💬', '💰', '📊', '🤝', '🎯']
    
    // Gradient colors for service cards
    const serviceGradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
    ]
    
    // Icons for service cards
    const serviceIcons = ['💰', '📊', '🤝', '🔐', '📱', '⚡']
    
    // Steps for how it works
    const steps = ref([
      {
        title: 'ثبت‌نام',
        description: 'با شماره موبایل خود ثبت‌نام کنید و رمز عبور بسازید'
      },
      {
        title: 'انتخاب شرکت',
        description: 'شرکت مورد نظر خود را از لیست شرکت‌ها انتخاب کنید'
      },
      {
        title: 'ورود به پنل',
        description: 'با نقش مناسب وارد پنل شوید و از خدمات استفاده کنید'
      }
    ])
    
    // Hero background based on company
    const heroBackground = computed(() => {

      const theme = companyData.value?.landing_theme || 'classic'

      const themes = {
        classic: 'linear-gradient(135deg,#667eea,#764ba2)',
        dark: 'linear-gradient(135deg,#1e293b,#0f172a)',
        ocean: 'linear-gradient(135deg,#0ea5e9,#2563eb)',
        sunset: 'linear-gradient(135deg,#fb7185,#f97316)',
        emerald: 'linear-gradient(135deg,#10b981,#047857)'
      }

      return {
        backgroundImage: themes[theme] || themes.classic
      }
    })
    
    // Load Neshan script dynamically
    const loadNeshanScript = () => {
      return new Promise((resolve, reject) => {
        if (typeof nmp !== 'undefined') {
          resolve(nmp)
          return
        }
        
        const script = document.createElement('script')
        script.src = 'https://static.neshan.org/api/mapbox-gl/v1.1.5/nmp.gl.js'
        script.onload = () => {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = 'https://static.neshan.org/api/mapbox-gl/v1.1.5/nmp.css'
          document.head.appendChild(link)
          resolve(window.nmp)
        }
        script.onerror = reject
        document.head.appendChild(script)
      })
    }
    
    // Initialize map with Neshan
    const initMap = async () => {
      if (!mapElement.value || !companyData.value?.location?.lat || !companyData.value?.location?.lng) return
      
      try {
        await loadNeshanScript()
        
        await new Promise(resolve => setTimeout(resolve, 100))
        
        const nmpLib = window.nmp || nmp
        
        const { lat, lng } = companyData.value.location
        
        map = new nmpLib.Map({
          mapKey: 'web.73a02790728f4a38862451622ccb92d7',
          container: mapElement.value,
          center: [lng, lat],
          zoom: 14,
          mapType: 'neshanVectorNight',
        })
        
        map.on('load', () => {
          console.log('Map loaded successfully')
          
          marker = new nmpLib.Marker({
            draggable: false
          })
            .setLngLat([lng, lat])
            .addTo(map)
          
          const popup = new nmpLib.Popup({ offset: 25 })
            .setHTML(`<div style="padding: 5px; color: black"><strong>${companyData.value.name}</strong><br>${companyData.value.address || ''}</div>`)
          marker.setPopup(popup)
          marker.togglePopup()
        })
        
        map.on('error', (e) => {
          console.error('Map error:', e)
        })
      } catch (error) {
        console.error('Error initializing map:', error)
      }
    }
    
    // Fetch company details
    const fetchCompanyDetails = async () => {
      const identifier = effectiveCompanyId.value || effectiveCompanyName.value
      
      if (!identifier) {
        error.value = 'شناسه شرکت نامعتبر است'
        loading.value = false
        return
      }
      console.log('identifier '+identifier)
      
      try {
        const response = await companyService.getPublicCompanyDetails(identifier)
        
        if (response.data.success) {
          companyData.value = response.data.data
          
          // Save to localStorage for later use
          localStorage.setItem('lastVisitedCompany', companyData.value.id)
          localStorage.setItem('lastVisitedCompanyName', companyData.value.name)
          savedCompanyId.value = companyData.value.id
          
          console.log('Company data received:', companyData.value)
          
          // Initialize map after data is loaded
          nextTick(() => {
            setTimeout(() => {
              initMap()
            }, 500)
          })
        } else {
          error.value = 'خطا در دریافت اطلاعات شرکت'
        }
      } catch (err) {
        console.error('Error fetching company details:', err)
        error.value = err.response?.data?.detail || 'خطا در ارتباط با سرور'
      } finally {
        loading.value = false
      }
    }
    
    // Format number with Persian digits
    const formatNumber = (num) => {
      if (num === undefined || num === null) return '۰'
      return new Intl.NumberFormat('fa-IR').format(num)
    }
    
    // Handle login
    const handleLogin = () => {
      if (effectiveCompanyId.value) {
        localStorage.setItem('intendedCompany', effectiveCompanyId.value)
      }
      router.push({
        path: '/login',
        query: effectiveCompanyId.value ? { companyId: String(effectiveCompanyId.value) } : {}
      })
    }
    
    // Go to company dashboard (if authenticated)
    const goToCompanyDashboard = () => {
      if (authStore.hasCompanySelected && authStore.getSelectedCompanyId == effectiveCompanyId.value) {
        router.push(`/dashboard`)
      } else {
        router.push({
          path: '/select-company',
          query: { 
            redirect: `/company/${companyData.value.url_friendly_name || effectiveCompanyName.value}`,
            companyId: effectiveCompanyId.value
          }
        })
      }
    }
    
    // Scroll to features
    const scrollToFeatures = () => {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
    }
    
    const goBack = () => {
      router.push('/select-role')
    }
    
    const goLanding = () => {
      router.push('/')
    }

    const themeClass = computed(() => {
      return `theme-${companyData.value?.landing_theme || 'classic'}`
    })

    const layoutClass = computed(() => {
      return `layout-${companyData.value?.landing_layout || 'default'}`
    })

    const companyLoginUrl = computed(() => {
      if (!companyData.value?.id) return ''
      const loginUrl = new URL('/login', window.location.origin)
      loginUrl.searchParams.set('companyId', String(companyData.value.id))
      return loginUrl.toString()
    })

    const companyLoginQrUrl = computed(() =>
      companyLoginUrl.value
        ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(companyLoginUrl.value)}`
        : ''
    )

    const copyCompanyLoginLink = async () => {
      try {
        await navigator.clipboard.writeText(companyLoginUrl.value)
        window.alert('لینک ورود شرکت کپی شد.')
      } catch (copyError) {
        console.error('Could not copy company login link:', copyError)
        window.prompt('لینک ورود شرکت را کپی کنید:', companyLoginUrl.value)
      }
    }

    
    // Initialize
    onMounted(() => {
      fetchCompanyDetails()
    })
    
    return {
      authStore,
      companyData,
      loading,
      error,
      isAuthenticated,
      savedCompanyId,
      effectiveCompanyId,
      companyServices,
      hasServices,
      whySelectItems,
      hasWhySelectItems,
      defaultIcons,
      serviceGradients,
      serviceIcons,
      steps,
      heroBackground,
      mapElement,
      router,
      formatNumber,
      handleLogin,
      goBack,
      goLanding,
      goToCompanyDashboard,
      scrollToFeatures,
      themeClass,
      layoutClass,
      companyLoginUrl,
      companyLoginQrUrl,
      copyCompanyLoginLink,

    }
  }
}
</script>

<style>
.btn-back {
  background: var(--bg);
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.btn-back:hover {
  background: var(--hover);
  transform: scale(1.1);
}

/* Main Container */
.public-company-landing {
  direction: rtl;
  overflow-x: hidden;
  color: var(--text);
  background: var(--bg);
}

/* Hero Section */
.hero-section {
  position: relative;
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  overflow: hidden;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 1000px;
  padding: 2rem;
  animation: fadeInUp 1s ease-out;
}

.company-badge {
  margin-bottom: 2rem;
}

.badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.9rem;
  margin: 0 0.25rem;
  backdrop-filter: blur(10px);
}

.badge.verified {
  background: var(--green);
  color: white;
}

.badge.since {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.company-name {
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 0 4px 20px var(--shadow-2);
  animation: fadeInUp 1s ease-out 0.2s both;
  color: white;
}

.company-description {
  font-size: 1.3rem;
  margin-bottom: 1rem;
  line-height: 1.8;
  opacity: 0.95;
  animation: fadeInUp 1s ease-out 0.25s both;
  color: white;
}

.company-full-description {
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.8;
  opacity: 0.9;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  animation: fadeInUp 1s ease-out 0.3s both;
  color: white;
}

.company-meta {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 3rem;
  animation: fadeInUp 1s ease-out 0.35s both;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  backdrop-filter: blur(5px);
}

.meta-icon {
  font-size: 1.2rem;
}

.meta-text {
  font-size: 0.95rem;
  color: white;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 2rem;
  animation: fadeInUp 1s ease-out 0.4s both;
}

.btn {
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-large {
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
}

.btn-primary {
  background: white;
  color: var(--primary);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px var(--shadow-2);
}

.btn-success {
  background: var(--green);
  color: white;
}

.btn-success:hover {
  background: #38a169;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: white;
}

.btn-icon {
  font-size: 1.2rem;
}

.saved-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 215, 0, 0.2);
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  border: 1px solid gold;
  animation: fadeInUp 1s ease-out 0.45s both;
}

.indicator-icon {
  color: gold;
  font-size: 1.2rem;
}

/* Stats Section */
.stats-section {
  padding: 4rem 0;
  background: var(--bg);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
}

.stat-card {
  text-align: center;
  padding: 2rem;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-1);
  transition: transform 0.3s;
  border: 1px solid var(--card-border);
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--primary);
  margin-bottom: 0.5rem;
}

.stat-label {
  color: var(--subtext);
  font-size: 1rem;
}

/* Features Section */
.features-section {
  padding: 5rem 0;
  background: var(--card-bg);
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--heading);
  margin-bottom: 1rem;
}

.section-subtitle {
  text-align: center;
  font-size: 1.2rem;
  color: var(--subtext);
  margin-bottom: 3rem;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.feature-card {
  background: var(--bg);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s;
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
  border: 1px solid var(--card-border);
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px var(--shadow-1);
}

.feature-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
}

.feature-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--heading);
  margin-bottom: 1rem;
}

.feature-description {
  color: var(--subtext);
  line-height: 1.6;
}

/* How It Works */
.how-it-works {
  padding: 5rem 0;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
}

.how-it-works .section-title {
  color: white;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
}

.step-card {
  text-align: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  position: relative;
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
}

.step-card:nth-child(1) { animation-delay: 0.1s; }
.step-card:nth-child(2) { animation-delay: 0.2s; }
.step-card:nth-child(3) { animation-delay: 0.3s; }

.step-number {
  width: 50px;
  height: 50px;
  background: white;
  color: var(--primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 auto 1.5rem;
}

.step-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.step-description {
  opacity: 0.9;
  line-height: 1.6;
}

.text-center {
  text-align: center;
}

/* Trust Section */
.trust-section {
  padding: 5rem 0;
  background: var(--bg);
}

.trust-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 3rem;
}

.trust-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  padding: 2rem;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-1);
  border: 1px solid var(--card-border);

  transition: transform 0.3s;
  gap: 0.75rem;
}

.trust-item:hover {
  transform: translateY(-5px);
}

.trust-icon {
  font-size: 3rem;
  line-height: 1;
}

.trust-item h4 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--heading);

  margin: 0;
}

.trust-item p {
  color: var(--subtext);
  font-size: 0.95rem;
  line-height: 1.6;

  margin: 0;
}

/* CTA Section */
.cta-section {
  padding: 5rem 0;
  background: var(--card-bg);
  text-align: center;
}

.cta-section h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--heading);
  margin-bottom: 1rem;
}

.cta-section p {
  font-size: 1.2rem;
  color: var(--subtext);
  margin-bottom: 2rem;
}

/* Footer */
.footer {
  background: var(--header-1);
  color: white;
  padding: 3rem 0;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.footer-info h4 {
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: white;
}

.footer-info p {
  color: var(--muted);
  margin: 0.25rem 0;
}

.footer-links {
  display: flex;
  gap: 2rem;
  justify-content: center;
}

.company-login-share {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  max-width: 460px;
  margin: 2rem auto 0;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
}

.company-login-share h3,
.company-login-share p {
  margin: 0;
  color: #fff;
}

.company-login-share p {
  line-height: 1.7;
}

.company-login-qr {
  display: block;
  width: 220px;
  height: 220px;
  padding: 8px;
  border-radius: 12px;
  background: #fff;
}

.footer-links a {
  color: var(--muted);
  text-decoration: none;
  transition: color 0.3s;
}

.footer-links a:hover {
  color: white;
}

.footer-copyright {
  text-align: center;
  color: var(--muted);
  font-size: 0.9rem;
  padding-top: 2rem;
  border-top: 1px solid var(--card-border);
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid var(--card-border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Error Overlay */
.error-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.error-card {
  background: var(--card-bg);
  padding: 3rem;
  border-radius: 16px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 20px 60px var(--shadow-2);
  border: 1px solid var(--card-border);
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-card h3 {
  font-size: 1.5rem;
  color: var(--orange-1);
  margin-bottom: 1rem;
}

.error-card p {
  color: var(--subtext);
  margin-bottom: 2rem;
}

/* Company Logo */
.company-logo-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.company-logo {
  max-width: 150px;
  max-height: 150px;
  border-radius: 50%;
  border: 4px solid white;
  box-shadow: 0 4px 20px var(--shadow-1);
  background: white;
  padding: 5px;
}

/* Website Link */
.website-link {
  margin: 20px 0;
}

.btn-website {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
}

.btn-website:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Address Info */
.address-info {
  margin: 15px 0;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  border-radius: 50px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-size: 0.95rem;
}

.address-icon {
  font-size: 1.2rem;
}

.post-code {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
}

/* Service Image */
.service-image-container {
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
  margin-bottom: 15px;
}

.service-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.feature-card:hover .service-image {
  transform: scale(1.05);
}

/* Map Section */
.map-section {
  padding: 60px 0;
  background: var(--bg);
}

.map-container {
  margin-top: 30px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px var(--shadow-1);
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 968px) {
  .company-name {
    font-size: 3rem;
  }
  
  .stats-grid,
  .trust-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .steps-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .company-name {
    font-size: 2.5rem;
  }
  
  .company-meta {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
  
  .cta-buttons {
    flex-direction: column;
  }
  
  .stats-grid,
  .features-grid,
  .steps-grid,
  .trust-grid {
    grid-template-columns: 1fr;
  }
  
  .section-title {
    font-size: 2rem;
  }
}
.why-select-section {
  padding: 5rem 0;
  background: var(--bg);
}

.why-select-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.why-select-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s;
  border: 1px solid var(--card-border);
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
}

.why-select-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px var(--shadow-1);
}

.why-select-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.why-select-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--heading);
  margin-bottom: 0.75rem;
}

.why-select-description {
  color: var(--subtext);
  line-height: 1.6;
}

/* Map container styles */
.map-container {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px var(--shadow-1);
}

@media (max-width: 992px) {
  .trust-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .trust-grid {
    grid-template-columns: 1fr;
  }
}

/* compact layout */
.layout-compact .section-title {
  font-size: 1.8rem;
}

.layout-compact .hero-section {
  min-height: 60vh;
}

.layout-compact .features-grid {
  gap: 1rem;
}

.layout-compact .feature-card {
  padding: 1.2rem;
}

/* full layout */
.layout-full .container {
  max-width: 1400px;
}

.layout-full .hero-section {
  min-height: 90vh;
}

.layout-full .company-name {
  font-size: 4.5rem;
}

.layout-full .features-grid {
  grid-template-columns: repeat(auto-fit,minmax(320px,1fr));
}

/* minimal layout */
.layout-minimal .stats-section {
  display:none;
}

.layout-minimal .cta-section {
  padding:3rem 0;
}

.layout-minimal .hero-section {
  min-height:60vh;
}
.footer-enamad {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px 0;
  border-top: 1px solid rgba(255,255,255,0.1);
  margin-top: 15px;
}

.enamad-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.enamad-link {
  display: inline-block;
  transition: transform 0.2s ease;
  text-decoration: none;
}

.enamad-link:hover {
  transform: scale(1.05);
}

.enamad-image {
  max-height: 80px;
  width: auto;
  object-fit: contain;
  border-radius: 4px;
}

.enamad-code {
  font-size: 14px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 12px;
  border-radius: 4px;
  font-family: monospace;
}
</style>
